import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditGymnastModal({ gymnast, categories, onClose, onUpdate }) {
  const [name, setName] = useState(gymnast.name);
  const [categoryId, setCategoryId] = useState(gymnast.categoryId);
  const [volume, setVolume] = useState(gymnast.volume || 35);
  const [mp3File, setMp3File] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('categoryId', categoryId);
      formData.append('volume', volume.toString());
      
      if (mp3File) {
        formData.append('mp3File', mp3File);
      }

      const token = localStorage.getItem('adminToken');
      await axios.put(`${API}/gymnasts/${gymnast.id}`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Gymnast updated successfully!');
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error updating gymnast:', error);
      toast.error('Failed to update gymnast');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1656A4] rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Edit Gymnast</h2>
          <button onClick={onClose} className="hover:bg-white/10 p-1 rounded">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-semibold mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-field w-full"
              required
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-semibold mb-1">Category</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="input-field w-full"
              required
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Volume */}
          <div>
            <label className="block text-sm font-semibold mb-2">
              Volume: {volume}%
            </label>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(parseInt(e.target.value))}
              className="w-full h-2 rounded-lg appearance-none cursor-pointer volume-slider"
              style={{
                background: `linear-gradient(to right, #ff3131 0%, #ff3131 ${volume}%, rgba(255, 255, 255, 0.2) ${volume}%, rgba(255, 255, 255, 0.2) 100%)`
              }}
            />
            <div className="flex justify-between text-xs mt-1 opacity-70">
              <span>Quiet</span>
              <span>Normal (35%)</span>
              <span>Loud</span>
            </div>
          </div>

          {/* MP3 File (optional) */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Replace Floor Music (Optional)
            </label>
            <input
              type="file"
              accept="audio/mpeg,audio/mp3"
              onChange={(e) => setMp3File(e.target.files[0])}
              className="input-field w-full"
            />
            {mp3File && (
              <p className="text-xs mt-1 opacity-70">
                Selected: {mp3File.name}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-white/10 hover:bg-white/20 py-2 rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
